import { Link } from "@tanstack/react-router";
import logoWhite from "@/assets/logo-white.webp";
import {
  InstagramIcon,
  LinkedInIcon,
  TikTokIcon,
  XIcon,
  YouTubeIcon,
} from "@/components/icons/social-icons";
import { SOCIAL_LINKS, type SocialPlatform } from "@/lib/seo-constants";

const SOCIAL_ICONS: Record<SocialPlatform, typeof LinkedInIcon> = {
  linkedin: LinkedInIcon,
  x: XIcon,
  tiktok: TikTokIcon,
  instagram: InstagramIcon,
  youtube: YouTubeIcon,
};

export function SiteFooter() {
  return (
    <footer className="relative mt-16 px-6 pt-20 pb-10 text-sm overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background: "linear-gradient(90deg, transparent, rgba(217,70,239,0.6), transparent)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(80% 100% at 50% 0%, rgba(217,70,239,0.22), transparent 70%), radial-gradient(60% 80% at 100% 100%, rgba(139,92,246,0.18), transparent 70%), radial-gradient(60% 80% at 0% 100%, rgba(236,72,153,0.14), transparent 70%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2 flex flex-col gap-4">
          <Link to="/" className="flex items-center" aria-label="Axy.digital home">
            <img src={logoWhite} alt="Axy.digital" width={225} height={91} className="h-7 w-auto" />
          </Link>
          <p className="text-white/60 max-w-[36ch]">
            Axy.digital is the AI-native marketing tool that automates SEO & GEO with minimal manual
            input, get agency-level outcomes at 1% of cost.
          </p>
          <div className="flex items-center gap-4">
            {SOCIAL_LINKS.map(({ platform, label, url }) => {
              const Icon = SOCIAL_ICONS[platform];
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-white/55 hover:text-white transition-colors"
                >
                  <Icon className="h-5 w-5" />
                </a>
              );
            })}
          </div>
        </div>
        <FooterCol
          title="Product"
          links={[
            ["/products", "Products"],
            ["/lp/intelligent-pr", "Intelligent PR"],
            ["/pricing", "Pricing"],
            ["/use-cases", "Use cases"],
          ]}
        />
        <FooterCol
          title="Resources"
          links={[
            ["/blog", "Blog"],
            ["/market-reports", "Market reports"],
            ["/case-studies", "Case studies"],
            ["/tutorials", "Tutorials"],
            ["/geo", "GEO fundamentals"],
            ["/comparisons", "Comparisons"],
            ["/affiliates", "Affiliates"],
          ]}
        />
        <FooterCol
          title="Company"
          links={[
            ["/about", "About"],
            ["/privacy", "Privacy"],
            ["/terms", "Terms"],
          ]}
        />
      </div>
      <div className="relative max-w-7xl mx-auto mt-12 pt-8 border-t border-white/10 text-xs text-white/50">
        © 2026 Auxetic Studio. All rights reserved.
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-[11px] uppercase tracking-[0.22em] text-pink-soft/90">{title}</span>
      {links.map(([to, label]) =>
        to.startsWith("/") ? (
          <Link key={label} to={to} className="text-white/65 hover:text-white transition-colors">
            {label}
          </Link>
        ) : (
          <a key={label} href={to} className="text-white/65 hover:text-white transition-colors">
            {label}
          </a>
        ),
      )}
    </div>
  );
}
